import React, {useEffect, useRef, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select';
import {Pagination} from '~/components/Pagination';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {Table} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {downloadTxt} from '~/lib/file';
import {Copy} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {controllableUseResource} from '~/lib/loader';
import {clientsLinks} from '~/modules/Clients';
import {SelectAgency} from '~/modules/Clients/components/SelectAgency';
import {creativesLinks} from '~/modules/Creatives';
import {CreativesAbility} from '~/modules/Creatives/ability';
import {fetchCreatives, ICreativeFilter} from '~/modules/Creatives/api';
import {adTypes, getAdTypeLabel} from '~/modules/Creatives/models/Creative';
import style from './CreativesView.module.scss';

export const CreativesView = () => {
    const ability = useAbility(CreativesAbility);
    const didMount = useRef(false);

    const [submitted, setSubmitted] = useState(0);

    const [filters, setFilters] = useState<ICreativeFilter>({
        agencyId: '',
        adCategory: null,
        adDomain: '',
        cid: '',
        crid: ''
    });

    const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({page: 0, pageSize: null});

    const isSellerOrSelfServe = ability.isSeller() || ability.isSelfServe();

    if (isSellerOrSelfServe) {
        if (filters.adDomain !== '-1' && filters.agencyId !== '-1') {
            filters.agencyId = '-1';
            filters.adDomain = '-1';
        } else {
            filters.agencyId = '';
            filters.adDomain = '';
        }
    }

    const result = controllableUseResource(() => fetchCreatives(filters, {
        pageSize: paginator.pageSize,
        pageNo: paginator.page
    }), {items: [], count: 0, page: 1, pageSize: 0});

    useEffect(() => {
        if (didMount.current) {
            result.fetch();
        } else {
            didMount.current = true;
        }
    }, [paginator]);

    const submitHandler = (event) => {
        setSubmitted(1);
        event.preventDefault();

        if (isSellerOrSelfServe) {
            filters.agencyId = '';
            filters.adDomain = '';
            if (!filters.cid && !filters.crid) {
                return false;
            }
        }

        result.fetch();
    };

    return <Breadcrumb breadcrumb={{
        to: creativesLinks.creatives(),
        title: 'Creatives'
    }}><BreadcrumbsResourcePage title='Creatives'>
            <div className="layout-content-box">
                <div>
                    <form className={style.creativesFilter + (isSellerOrSelfServe ? ' ' + style.short : '') +
                    (submitted ? ' ' + style.isSubmitted : '')} onSubmit={submitHandler}>
                        {!isSellerOrSelfServe &&
                        <SelectAgency value={filters.agencyId} onChange={agencyId => setFilters({...filters, agencyId})}
                            placeholder='Agency' clearable searchable disabled={result.isPending}
                            className={style.agencySelect}/>
                        }
                        {!isSellerOrSelfServe &&
                        <SelectInput items={adTypes} value={filters.adCategory}
                            onChange={adCategory => setFilters({...filters, adCategory})}
                            placeholder='Ad Type' disabled={result.isPending} clearable
                            className={style.adTypeSelect}/>
                        }
                        {!isSellerOrSelfServe &&
                        <Input
                            value={filters.adDomain}
                            onChange={adDomain => setFilters({...filters, adDomain})}
                            placeholder='Domain'
                            disabled={result.isPending}
                            className={style.domainInput}
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />}
                        <Input
                            value={filters.cid}
                            onChange={cid => setFilters({...filters, cid})}
                            placeholder='CID'
                            disabled={result.isPending}
                            className={style.cidInput}
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />
                        <Input
                            value={filters.crid}
                            onChange={crid => setFilters({...filters, crid})}
                            placeholder='CRID'
                            disabled={result.isPending}
                            className={style.cridInput}
                            rightIcon={<i className='fa fa-search input-search-icon' />}
                        />
                        <Button onClick={() => setSubmitted(1)} type={'submit'} rightIcon={<i className='fa fa-search'/>}
                            intent={Intent.PRIMARY}
                            disabled={result.isPending || (isSellerOrSelfServe && (!filters.cid && !filters.crid))}
                            className={style.searchButton}>Search</Button>
                    </form>
                </div>
                {result.isPending &&
                <div className={style.infoMessage}>Databricks endpoint may be turned off, so your query may take 5-7
                    minutes</div>}
                <Table header
                    columns={[{
                    header: 'Updated',
                    field: 'updated',
                    cell: creative => new Date(creative.updated).toLocaleString()
                }, {
                    header: 'Ad Type',
                    field: 'adCategory',
                    cell: creative => getAdTypeLabel(creative.adCategory)
                }, {
                    header: 'Agency',
                    cell: creative => isSellerOrSelfServe ? creative.agencyName : <NavLink
                        to={clientsLinks.agency(creative.agencyId)}>{creative.agencyName}</NavLink>
                }, {
                    header: 'CID',
                    field: 'cid'
                }, {
                    header: 'CRID',
                    field: 'crid'
                }, {
                    header: 'Domain',
                    field: 'adDomain'
                }, {
                    header: 'iURL',
                    cell: creative => <Copy
                        text={creative.iURL}/>
                }, {
                    header: 'Adm',
                    cell: creative => <Button small
                        responsive
                        intent={Intent.DEFAULT}
                        onClick={() =>
                            downloadTxt(`agency_${creative.agencyId}_cid_${creative.cid}_crid_${creative.crid}.txt`, creative.adm || '')}
                        leftIcon={<i
                            className='fa fa-download'/>}/>
                }]}
                    records={result.resource.items}
                    placeholder={result.isPending && <Progress/>}/>
                {result.resource.count > result.resource.pageSize &&
                <Pagination padding={4} offset={result.resource.pageSize * result.resource.page}
                    limit={result.resource.pageSize}
                    total={result.resource.count} onChange={(page, perPage) => {
                        setPaginator({page, pageSize: perPage});
                    }}/>
                }
            </div>
        </BreadcrumbsResourcePage></Breadcrumb>;
};
